import type { FormEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { AuthStore } from '../state/useAuth';

/**
 * The VisitIQ sign-in dialog. Opened from the sidebar's account block when
 * signed out, and from the toolbar's audience warning chip - both just need
 * `auth` and a close handler.
 *
 * The password lives in component state for the length of one submit and is
 * cleared immediately. It is never stored, cached or logged on this side.
 */
export function LoginModal({ auth, onClose }: { auth: AuthStore; onClose: () => void }): ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const secret = password;
    setPassword('');
    void auth.signIn(username, secret).then((ok) => {
      if (ok) onClose();
    });
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Sign in to VisitIQ"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="modal-title">Sign in to VisitIQ</h2>
        <p className="modal-help">
          Your VisitIQ login unlocks real audience counts. Without it, estimates come from a
          local metro table and are labelled as placeholders.
        </p>
        <form onSubmit={submit}>
          <label className="field">
            <span>Username</span>
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              autoFocus
            />
          </label>
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          {auth.error ? <p className="modal-error">{auth.error}</p> : null}
          <div className="modal-actions">
            <button type="button" className="chrome-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={auth.busy}>
              {auth.busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
